import { formatDate, getSelectioAxios, isEmpty } from '@/main';
import { normalizeEmptyValue } from '@/ag-grid-helpers';

export default class BuyerService {

  async updateEasynumbers(buyers, username, companyId, idPortfolio) {
    return getSelectioAxios().post('/legacy-buyer/update-easynumbers', {
        userUpdate: username,
        companyId: companyId,
        idPortfolio: isEmpty(idPortfolio) ? 0 : idPortfolio,
        buyers: buyers,
      })
      .then(response => {
        return response.data.entity;
      });
  }

  async countTotalActiveBuyers(idPortfolio) {
    return this.buyersSearch({
        filters: [
          { fieldId: 'portfolioId', match: 'eq', value: Number(idPortfolio) },
          { fieldId: 'deleted', match: 'eq', value: 'N' },
        ],
        returnData: { count: true, items: false },
      })
      .then((data) => data.count);
  }

  async buyersSearch(search) {
    return getSelectioAxios().post('/buyers/search', search)
      .then(response => response.data);
  }

  async buyersUnmatchedSearch(search) {
    return getSelectioAxios().post('/buyers/unmatched/search', search)
      .then(response => response.data);
  }

  //region Custom Data
  async getCustomDataByPortfolio(portfolioId) {
    return getSelectioAxios().get(`/buyers/custom-data/portfolios/${portfolioId}`)
      .then(response => response.data);
  }

  async getCustomDataFieldsByPortfolio(portfolioId) {
    return getSelectioAxios().get(`/buyers/custom-data/portfolios/${portfolioId}/fields`)
      .then(response => response.data);
  }

  async getCustomDataMapByPortfolio(portfolioId) {
    const map = {};
    const rows = await this.getCustomDataByPortfolio(portfolioId);
    // console.log('getCustomDataMapByPortfolio', rows);
    for (const row of rows) {
      const buyerId = row.buyerId;
      map[buyerId] = map[buyerId] || {};
      map[buyerId][row.label] = {
        value: row.value,
        id: row.customDataId,
        label: row.label,
      };
    }
    // console.log('getCustomDataMapByPortfolio', map);
    return map;
  }

  //endregion

  //region Fiscal Data
  async getMainFiscalDataByPortfolio(portfolioId) {
    return getSelectioAxios().get(`/buyers/fiscal-data/portfolios/${portfolioId}/mainFiscalRefs/minimal`)
      .then(response => response.data);
  }

  async getMainFiscalDataMapByPortfolio(portfolioId) {
    return this.getMainFiscalDataByPortfolio(portfolioId)
      .then(rows => {
        return {
          ...Object.fromEntries(rows
            .map(r => [ r.buyerId, r.value ])),
        };
      });
  }

  async getFiscalDataByPortfolio(portfolioId) {
    return getSelectioAxios().get(`/buyers/fiscal-data/portfolios/${portfolioId}`)
      .then(response => response.data);
  }

  async getFiscalDataMapByPortfolio(portfolioId) {
    return this.getFiscalDataByPortfolio(portfolioId)
      .then(rows => this.transposeFiscalDataListToMap(rows));
  }

  async getFiscalDataByBuyerIds(buyerIds) {
    return getSelectioAxios().post('/buyers/fiscal-data/by-buyer-ids', buyerIds)
      .then(response => response.data);
  }

  async getFiscalDataMapByBuyerIds(buyerIds) {
    return this.getFiscalDataByBuyerIds(buyerIds)
      .then(rows => this.transposeFiscalDataListToMap(rows));
  }

  transposeFiscalDataListToMap(fiscalDataList) {
    const map = {};
    for (const row of fiscalDataList) {
      // consoe.log(row);
      const buyerId = row.buyerId;
      map[buyerId] = map[buyerId] || {
        mainFiscalRef: null,
        deleted: row.deleted,
        fiscalData: [],
      };

      map[buyerId].fiscalData.push({
        id: row.id,
        value: row.value,
        identifierId: row.fiscalIdentifier.id,
        code: row.fiscalIdentifier.code,
        priority: row.fiscalIdentifier.priority,
      });
    }

    Object.keys(map).forEach(buyerId => {
      // Sort fiscal identifiers by priority
      map[buyerId].fiscalData.sort((a, b) => a.priority - b.priority);
      map[buyerId].mainFiscalRef = map[buyerId].fiscalData[0] ? map[buyerId].fiscalData[0].value : null;
    });
    return map;
  }

  //endregion

  //region Customer Refs
  async getCustomerRefsByPortfolio(portfolioId) {
    return getSelectioAxios().get(`/buyers/customer-refs/portfolios/${portfolioId}`)
      .then(response => response.data);
  }

  async getCustomerRefMapByPortfolio(portfolioId) {
    const rows = await this.getCustomerRefsByPortfolio(portfolioId);
    const map = {};
    for (const row of rows) {
      if (normalizeEmptyValue(row.customerRef))
        map[row.buyerId] = row.customerRef;
    }
    return map;
  }

  //endregion

  logRichiestaCopertura(idBuyer, dataOsservazione, dra, fidoCommerciale, coverType, coverAmount) {
    const variables = {
      'idBuyer': idBuyer,
      'dataOsservazione': dataOsservazione,
      'dra': dra,
      'fidoCommerciale': fidoCommerciale,
      'coverType': coverType,
      'coverAmount': coverAmount,
    };
    return getSelectioAxios().post('/buyers/' + idBuyer + '/coverage-requests', variables);
  }

  getHistoricalAnalysesByBuyer(idBuyer, reportDate) {
    const variables = {
      'idBuyer': idBuyer,
      'dataOsservazione': formatDate(reportDate, 'yyyy-mm-dd'),
    };
    return getSelectioAxios()
      .get('/analyses/historical-analyses/' + variables.dataOsservazione + '/buyers/' + variables.idBuyer)
      .then(res => res.data);
  }

  getSchedaBuyerPdf(params, fileName, buyerId) {
    return getSelectioAxios()
      .post('/buyers/' + buyerId + '/scheda-buyer-pdf', params)
      .then(response => {
        let link = window.document.createElement('a');
        link.href = 'data:application/pdf;base64,' + response.data.result;
        link.download = fileName;
        link.click();
      });
  }

  getRapportoInformativoPdf(idHistoricAnalysis, reportDate, coverageDate, fileName, coverValue, logId, languageCode) {
    const variables = {
      'idHistoricAnalisys': idHistoricAnalysis,
      'reportDate': reportDate,
      'coverageDate': coverageDate,
      'coverValue': coverValue,
      'logId': logId.data,
      'languageCode': languageCode,
    };
    return getSelectioAxios()
      .post('/analyses/informative-report/' + idHistoricAnalysis, variables)
      .then(response => {
        console.log('rapportoInformativo response = \'' + response + '\'');
        let link = window.document.createElement('a');
        link.href = 'data:application/pdf;base64,' + response.data.result;
        link.download = fileName;
        link.click();
      });
  }

  findPurchasingBuyers(portfolioId) {
    return getSelectioAxios().get('/buyers/portfolios/' + portfolioId + '/purchasing')
      .then(result => result.data);
  }

  forceEasynumber(buyerId, easynumber) {
    if (isNaN(easynumber) || Number(easynumber) <= 0)
      return false;
    return getSelectioAxios().put('/buyers/' + buyerId + '/easynumber/' + easynumber)
      .then(result => result.data);
  }

  getCountTotalBuyersOfActiveCustomerPortfolios(companyId, agencyCode) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/customer-portfolios/active/count')
      .then(result => result.data);
  }

  getCountTotalBuyersOfActiveProspectPortfolios(companyId, agencyCode) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/prospect-portfolios/active/count')
      .then(result => result.data);
  }

  getCountTotalAnalizedBuyersOfActiveCustomerPortfolios(companyId, agencyCode) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/customer-portfolios/active/analyzed/count')
      .then(result => result.data);
  }

  getCountTotalAnalyzedBuyersOfActiveProspectPortfolios(companyId, agencyCode) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/prospect-portfolios/active/analyzed/count')
      .then(result => result.data);
  }

  countActiveBuyersByPortfolioId(portfolioId) {
    return getSelectioAxios().get('/buyers/portfolios/' + portfolioId + '/active/count')
      .then(result => result.data);
  }

  getCountTotalExposureByPortfolio(portfolioId) {
    return getSelectioAxios().get('/buyers/portfolios/' + portfolioId + '/exposure')
      .then(result => result.data);
  }

  getCountTotalExposureOfActiveCustomerPortfolios(companyId, agencyCode, periodId) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/periods/' + periodId + '/customer-portfolios/active/exposure')
      .then(result => result.data);
  }

  getCountTotalExposureOfActiveProspectPortfolios(companyId, agencyCode, periodId) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/periods/' + periodId + '/prospect-portfolios/active/exposure')
      .then(result => result.data);
  }

  getCountTotalExposureOfAnalysedActiveCustomerPortfolios(companyId, agencyCode, periodId) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/periods/' + periodId + '/customer-portfolios/active/analyzed/exposure')
      .then(result => result.data);
  }

  getCountTotalExposureOfAnalysedActiveProspectPortfolios(companyId, agencyCode, periodId) {
    return getSelectioAxios().get('/buyers/companies/' + companyId + '/agency/' + (isEmpty(agencyCode) ? 0 : agencyCode) + '/periods/' + periodId + '/prospect-portfolios/active/analyzed/exposure')
      .then(result => result.data);
  }
}

